import { TPagingResponse } from "../../../models/PagingResponse";
import { request } from "../../../network/mainClient/request";
import { IViewKeys } from "../../company/models";
import { TId } from "../../ui/models";
import {
  TApp,
  TAppSettings,
  TCreateAppData,
  TCreateDocumentTemplate,
  TDocumentTemplate,
  TSdkSettings,
  TUploadTemplateRespone,
  TGetAppTemplatesResponse,
} from "../models";
import type { AppSettingsSchema } from "../components/AppSettingsSchema/context";

export const getAppList = async (
  company_id: string
): Promise<TPagingResponse<TApp>> => {
  const response = await request({
    path: `/api/v1/verification-service/${company_id}/app`,
    method: "GET",
  });

  if (response.ok) {
    return {
      items: response.data.apps,
    };
  }

  throw new Error(response.errorMessage);
};

export const getApp = async (id: string): Promise<TApp> => {
  const response = await request({
    path: `/api/v1/verification-service/app/${id}`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.app;
  }

  throw new Error(response.errorMessage);
};

export const createApp = async (data: TCreateAppData): Promise<TApp> => {
  const response = await request({
    path: "/api/v1/verification-service/app",
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.app;
  }

  throw new Error(response.errorMessage);
};

export const getAppSettings = async (id: string): Promise<TAppSettings> => {
  const response = await request({
    path: `/api/v1/verification-service/app/${id}/settings`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.appSettings;
  }

  throw new Error(response.errorMessage);
};

export const getAppSettingsSchema = async (): Promise<AppSettingsSchema> => {
  const response = await request({
    path: "/api/v1/verification-service/app/settings/schema",
    method: "GET",
  });

  if (response.ok) {
    return response.data.schema;
  }

  throw response.apiError || new Error(response.errorMessage);
};

export const updateAppSettings = async (
  appId: string,
  data: TAppSettings
): Promise<TAppSettings> => {
  const response = await request({
    path: `/api/v1/verification-service/app/${appId}/settings`,
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.appSettings;
  }

  throw response.apiError || new Error(response.errorMessage);
};

export const updateSdkSettings = async (
  appId: string,
  data: TSdkSettings
): Promise<TAppSettings> => {
  const response = await request({
    path: `/api/v1/sdk/app/${appId}/settings`,
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.settings;
  }

  throw new Error(response.errorMessage);
};

export const getAppKeys = async (app_id: TId): Promise<IViewKeys> => {
  const response = await request({
    path: `/api/v1/user-service/app/${app_id}/keys`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.keys;
  }

  throw new Error(response.errorMessage);
};

export const createDocumentTemplate = async (
  data: TCreateDocumentTemplate
): Promise<TDocumentTemplate> => {
  const response = await request({
    path: "/api/v1/user-service/template",
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.template;
  }

  throw new Error(response.errorMessage);
};

export const associateDocumentTemplate = async (
  app_id: string,
  template_id: string
): Promise<TDocumentTemplate> => {
  const response = await request({
    path: `/api/v1/user-service/app/${app_id}/template/${template_id}`,
    method: "POST",
  });

  if (response.ok) {
    return response.data.template;
  }

  throw new Error(response.errorMessage);
};

export const updateDocumentTemplate = async (
  template_id: string,
  data: TCreateDocumentTemplate
): Promise<TDocumentTemplate> => {
  const response = await request({
    path: `/api/v1/user-service/template/${template_id}`,
    method: "POST",
    body: data,
  });

  if (response.ok) {
    return response.data.template;
  }

  throw new Error(response.errorMessage);
};

export const deleteDocumentTemplate = async (
  template_id: string
): Promise<TDocumentTemplate> => {
  const response = await request({
    path: `/api/v1/user-service/template/${template_id}`,
    method: "DELETE",
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const getTemplatesList = async (
  app_id: string
): Promise<{
  items: TDocumentTemplate[];
}> => {
  const response = await request({
    path: `/api/v1/user-service/template/list?app_id=${app_id}`,
    method: "GET",
  });

  if (response.ok) {
    return { items: response.data.templates };
  }

  throw new Error(response.errorMessage);
};

export const generateDocumentTemplateUploadUrl = async (
  template_id: string
): Promise<TUploadTemplateRespone> => {
  const response = await request({
    path: `/api/v1/user-service/template/${template_id}/version/upload-url`,
    method: "POST",
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const addDocumentTemplateVersion = async (
  template_id: string,
  version_description: string,
  file_path: string
): Promise<TUploadTemplateRespone> => {
  const response = await request({
    path: `/api/v1/user-service/template/${template_id}/version`,
    method: "POST",
    body: {
      version_description,
      file_path,
    },
  });

  if (response.ok) {
    return response.data.version;
  }

  throw new Error(response.errorMessage);
};

export const getAppTemplates = async (
  appId: string
): Promise<TGetAppTemplatesResponse> => {
  const response = await request({
    path: `/api/v1/user-service/app/${appId}/templates`,
    method: "GET",
  });

  if (response.ok) {
    return {
      items: response.data.templates || [],
    };
  }

  throw new Error(response.errorMessage);
};

export const getTemplateFile = async (
  template_id: string
): Promise<{ url: string }> => {
  const response = await request({
    path: `/api/v1/user-service/template/${template_id}/file`,
    method: "GET",
  });

  if (response.ok) {
    return { url: response.data.url };
  }

  throw new Error(response.errorMessage);
};
